import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  RefreshCw,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '../../components/EmptyState';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { LoadingSkeleton } from '../../components/LoadingSkeleton';
import { PriorityIndicator } from '../../components/PriorityIndicator';
import { StatusBadge } from '../../components/StatusBadge';
import type { DenialListFilters } from '../../lib/api';
import { api, ApiRequestError } from '../../lib/api';
import { friendlyError } from '../../lib/error_codes';
import type { DenialStatus } from '@nexaclaim/shared';

const STATUS_OPTIONS: { value: DenialStatus | ''; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'in_review', label: 'In Review' },
  { value: 'appeal_drafted', label: 'Appeal Drafted' },
  { value: 'appeal_submitted', label: 'Appeal Submitted' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'written_off', label: 'Written Off' },
];

function formatDollars(cents: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function formatProbability(p: number | null) {
  if (p === null) return '—';
  return `${Math.round(p * 100)}%`;
}

export function DenialsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [filters, setFilters] = useState<DenialListFilters>({
    sort: '-priority,-denied_amount_cents',
    per_page: 25,
  });
  const [sort, setSort] = useState<{ field: string; dir: 'asc' | 'desc' }>({
    field: 'priority',
    dir: 'desc',
  });

  const { data: denials, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['denials', filters],
    queryFn: () => api.denials.list(filters),
    refetchInterval: 30_000,
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => api.denials.upload(file),
    onSuccess: () => {
      toast.success('EDI 835 file uploaded. Processing has started.');
      qc.invalidateQueries({ queryKey: ['denials'] });
    },
    onError: (err) => {
      const code = err instanceof ApiRequestError ? err.code : 'UNKNOWN_ERROR';
      toast.error(friendlyError(code));
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    e.target.value = '';
  }

  function toggleSort(field: string) {
    const next = sort.field === field && sort.dir === 'asc' ? 'desc' : 'asc';
    setSort({ field, dir: next });
    setFilters((f) => ({
      ...f,
      sort: `${next === 'asc' ? '' : '-'}${field}`,
    }));
  }

  const SortIcon = useCallback(
    ({ field }: { field: string }) => {
      if (sort.field !== field) return null;
      return sort.dir === 'asc' ? (
        <ChevronUp className="ml-1 inline h-3 w-3" />
      ) : (
        <ChevronDown className="ml-1 inline h-3 w-3" />
      );
    },
    [sort],
  );

  return (
    <ErrorBoundary>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Denial Worklist</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {denials?.length ?? 0} denial{denials?.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 hover:bg-gray-50"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Refresh
            </button>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              <Upload className="h-3.5 w-3.5" />
              {uploadMutation.isPending ? 'Uploading…' : 'Upload 835'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.835,.edi"
              onChange={handleFileChange}
              className="hidden"
              aria-hidden
            />
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3">
          <select
            aria-label="Filter by status"
            value={filters.status ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                status: (e.target.value as DenialStatus) || undefined,
              }))
            }
            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-primary-600 focus:outline-none sm:w-auto"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          <select
            aria-label="Filter by priority"
            value={filters.priority ?? ''}
            onChange={(e) =>
              setFilters((f) => ({
                ...f,
                priority: e.target.value ? Number(e.target.value) : undefined,
              }))
            }
            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-primary-600 focus:outline-none sm:w-auto"
          >
            <option value="">All priorities</option>
            {[1, 2, 3, 4, 5].map((p) => (
              <option key={p} value={p}>
                P{p}
              </option>
            ))}
          </select>

          <input
            type="date"
            aria-label="From date"
            value={filters.date_from ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, date_from: e.target.value || undefined }))
            }
            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-primary-600 focus:outline-none sm:w-auto"
          />

          <input
            type="date"
            aria-label="To date"
            value={filters.date_to ?? ''}
            onChange={(e) =>
              setFilters((f) => ({ ...f, date_to: e.target.value || undefined }))
            }
            className="w-full rounded border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 focus:border-primary-600 focus:outline-none sm:w-auto"
          />

          {(filters.status || filters.priority || filters.date_from || filters.date_to) && (
            <button
              type="button"
              onClick={() =>
                setFilters({ sort: filters.sort, per_page: filters.per_page })
              }
              className="text-sm text-gray-500 hover:text-gray-900"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {isLoading ? (
            <div className="p-6">
              <LoadingSkeleton />
            </div>
          ) : isError ? (
            <div className="p-6 text-center text-sm text-red-600">
              {error instanceof ApiRequestError
                ? friendlyError(error.code)
                : 'Failed to load denials.'}
            </div>
          ) : !denials || denials.length === 0 ? (
            <EmptyState
              title="No denials found"
              description="Upload an EDI 835 file to import denials, or adjust your filters."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left">
                    <th
                      className="cursor-pointer px-4 py-3 font-medium text-gray-600"
                      onClick={() => toggleSort('priority')}
                    >
                      Priority
                      <SortIcon field="priority" />
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-600">Claim #</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Payer</th>
                    <th
                      className="cursor-pointer px-4 py-3 font-medium text-gray-600"
                      onClick={() => toggleSort('denied_amount_cents')}
                    >
                      Denied Amount
                      <SortIcon field="denied_amount_cents" />
                    </th>
                    <th className="px-4 py-3 font-medium text-gray-600">Category</th>
                    <th className="px-4 py-3 font-medium text-gray-600">CARC</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Recovery</th>
                    <th className="px-4 py-3 font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {denials.map((denial) => (
                    <tr
                      key={denial.id}
                      onClick={() => navigate(`/denials/${denial.id}`)}
                      className="cursor-pointer hover:bg-gray-50"
                    >
                      <td className="px-4 py-3">
                        <PriorityIndicator priority={denial.priority} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {denial.claim_id}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{denial.payer_id}</td>
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {formatDollars(denial.denied_amount_cents)}
                      </td>
                      <td className="px-4 py-3 capitalize text-gray-600">
                        {denial.category}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-600">
                        {denial.carc_code}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`font-medium ${
                            (denial.recovery_probability ?? 0) >= 0.7
                              ? 'text-green-600'
                              : (denial.recovery_probability ?? 0) >= 0.4
                                ? 'text-amber-600'
                                : 'text-gray-500'
                          }`}
                        >
                          {formatProbability(denial.recovery_probability)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={denial.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}
